/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type Codec,
  type Decoder,
  type EncodedAccount,
  type Encoder,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type Option,
  type OptionOrNullable,
  type ReadonlyUint8Array,
} from "@solana/kit";
import {
  getAgentStatusDecoder,
  getAgentStatusEncoder,
  getExternalIdentifierDecoder,
  getExternalIdentifierEncoder,
  getPricingModelDecoder,
  getPricingModelEncoder,
  getReputationComponentDecoder,
  getReputationComponentEncoder,
  type AgentStatus,
  type AgentStatusArgs,
  type ExternalIdentifier,
  type ExternalIdentifierArgs,
  type PricingModel,
  type PricingModelArgs,
  type ReputationComponent,
  type ReputationComponentArgs,
} from "../types";

export const AGENT_DISCRIMINATOR = new Uint8Array([
  47, 166, 112, 147, 155, 197, 86, 7,
]);

export function getAgentDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(AGENT_DISCRIMINATOR);
}

export type Agent = {
  discriminator: ReadonlyUint8Array;
  owner: Option<Address>;
  status: AgentStatus;
  agentId: string;
  firstTxSignature: string;
  firstSeenTimestamp: bigint;
  discoverySource: string;
  claimedAt: Option<bigint>;
  agentType: number;
  name: string;
  description: string;
  capabilities: Array<string>;
  pricingModel: PricingModel;
  reputationScore: number;
  totalJobsCompleted: number;
  totalEarnings: bigint;
  isActive: boolean;
  createdAt: bigint;
  updatedAt: bigint;
  originalPrice: bigint;
  genomeHash: string;
  isReplicable: boolean;
  replicationFee: bigint;
  serviceEndpoint: string;
  isVerified: boolean;
  verificationTimestamp: bigint;
  metadataUri: string;
  frameworkOrigin: string;
  supportedTokens: Array<Address>;
  cnftMint: Option<Address>;
  merkleTree: Option<Address>;
  supportsA2a: boolean;
  transferHook: Option<Address>;
  parentAgent: Option<Address>;
  generation: number;
  x402Enabled: boolean;
  x402PaymentAddress: Address;
  x402AcceptedTokens: Array<Address>;
  x402PricePerCall: bigint;
  x402ServiceEndpoint: string;
  x402TotalPayments: bigint;
  x402TotalCalls: bigint;
  lastPaymentTimestamp: bigint;
  externalIdentifiers: Array<ExternalIdentifier>;
  ghostScore: bigint;
  reputationComponents: Array<ReputationComponent>;
  didAddress: Option<Address>;
  credentials: Array<Address>;
  apiSpecUri: string;
  apiVersion: string;
  bump: number;
};

export type AgentArgs = {
  owner: OptionOrNullable<Address>;
  status: AgentStatusArgs;
  agentId: string;
  firstTxSignature: string;
  firstSeenTimestamp: number | bigint;
  discoverySource: string;
  claimedAt: OptionOrNullable<number | bigint>;
  agentType: number;
  name: string;
  description: string;
  capabilities: Array<string>;
  pricingModel: PricingModelArgs;
  reputationScore: number;
  totalJobsCompleted: number;
  totalEarnings: number | bigint;
  isActive: boolean;
  createdAt: number | bigint;
  updatedAt: number | bigint;
  originalPrice: number | bigint;
  genomeHash: string;
  isReplicable: boolean;
  replicationFee: number | bigint;
  serviceEndpoint: string;
  isVerified: boolean;
  verificationTimestamp: number | bigint;
  metadataUri: string;
  frameworkOrigin: string;
  supportedTokens: Array<Address>;
  cnftMint: OptionOrNullable<Address>;
  merkleTree: OptionOrNullable<Address>;
  supportsA2a: boolean;
  transferHook: OptionOrNullable<Address>;
  parentAgent: OptionOrNullable<Address>;
  generation: number;
  x402Enabled: boolean;
  x402PaymentAddress: Address;
  x402AcceptedTokens: Array<Address>;
  x402PricePerCall: number | bigint;
  x402ServiceEndpoint: string;
  x402TotalPayments: number | bigint;
  x402TotalCalls: number | bigint;
  lastPaymentTimestamp: number | bigint;
  externalIdentifiers: Array<ExternalIdentifierArgs>;
  ghostScore: number | bigint;
  reputationComponents: Array<ReputationComponentArgs>;
  didAddress: OptionOrNullable<Address>;
  credentials: Array<Address>;
  apiSpecUri: string;
  apiVersion: string;
  bump: number;
};

/** Gets the encoder for {@link AgentArgs} account data. */
export function getAgentEncoder(): Encoder<AgentArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["owner", getOptionEncoder(getAddressEncoder())],
      ["status", getAgentStatusEncoder()],
      ["agentId", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      [
        "firstTxSignature",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      ["firstSeenTimestamp", getI64Encoder()],
      [
        "discoverySource",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      ["claimedAt", getOptionEncoder(getI64Encoder())],
      ["agentType", getU8Encoder()],
      ["name", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["description", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      [
        "capabilities",
        getArrayEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      ["pricingModel", getPricingModelEncoder()],
      ["reputationScore", getU32Encoder()],
      ["totalJobsCompleted", getU32Encoder()],
      ["totalEarnings", getU64Encoder()],
      ["isActive", getBooleanEncoder()],
      ["createdAt", getI64Encoder()],
      ["updatedAt", getI64Encoder()],
      ["originalPrice", getU64Encoder()],
      ["genomeHash", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["isReplicable", getBooleanEncoder()],
      ["replicationFee", getU64Encoder()],
      [
        "serviceEndpoint",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      ["isVerified", getBooleanEncoder()],
      ["verificationTimestamp", getI64Encoder()],
      ["metadataUri", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      [
        "frameworkOrigin",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      ["supportedTokens", getArrayEncoder(getAddressEncoder())],
      ["cnftMint", getOptionEncoder(getAddressEncoder())],
      ["merkleTree", getOptionEncoder(getAddressEncoder())],
      ["supportsA2a", getBooleanEncoder()],
      ["transferHook", getOptionEncoder(getAddressEncoder())],
      ["parentAgent", getOptionEncoder(getAddressEncoder())],
      ["generation", getU32Encoder()],
      ["x402Enabled", getBooleanEncoder()],
      ["x402PaymentAddress", getAddressEncoder()],
      ["x402AcceptedTokens", getArrayEncoder(getAddressEncoder())],
      ["x402PricePerCall", getU64Encoder()],
      [
        "x402ServiceEndpoint",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      ["x402TotalPayments", getU64Encoder()],
      ["x402TotalCalls", getU64Encoder()],
      ["lastPaymentTimestamp", getI64Encoder()],
      ["externalIdentifiers", getArrayEncoder(getExternalIdentifierEncoder())],
      ["ghostScore", getU64Encoder()],
      [
        "reputationComponents",
        getArrayEncoder(getReputationComponentEncoder()),
      ],
      ["didAddress", getOptionEncoder(getAddressEncoder())],
      ["credentials", getArrayEncoder(getAddressEncoder())],
      ["apiSpecUri", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["apiVersion", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["bump", getU8Encoder()],
    ]),
    (value) => ({ ...value, discriminator: AGENT_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link Agent} account data. */
export function getAgentDecoder(): Decoder<Agent> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["owner", getOptionDecoder(getAddressDecoder())],
    ["status", getAgentStatusDecoder()],
    ["agentId", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    [
      "firstTxSignature",
      addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder()),
    ],
    ["firstSeenTimestamp", getI64Decoder()],
    [
      "discoverySource",
      addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder()),
    ],
    ["claimedAt", getOptionDecoder(getI64Decoder())],
    ["agentType", getU8Decoder()],
    ["name", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["description", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    [
      "capabilities",
      getArrayDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    ["pricingModel", getPricingModelDecoder()],
    ["reputationScore", getU32Decoder()],
    ["totalJobsCompleted", getU32Decoder()],
    ["totalEarnings", getU64Decoder()],
    ["isActive", getBooleanDecoder()],
    ["createdAt", getI64Decoder()],
    ["updatedAt", getI64Decoder()],
    ["originalPrice", getU64Decoder()],
    ["genomeHash", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["isReplicable", getBooleanDecoder()],
    ["replicationFee", getU64Decoder()],
    [
      "serviceEndpoint",
      addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder()),
    ],
    ["isVerified", getBooleanDecoder()],
    ["verificationTimestamp", getI64Decoder()],
    ["metadataUri", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    [
      "frameworkOrigin",
      addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder()),
    ],
    ["supportedTokens", getArrayDecoder(getAddressDecoder())],
    ["cnftMint", getOptionDecoder(getAddressDecoder())],
    ["merkleTree", getOptionDecoder(getAddressDecoder())],
    ["supportsA2a", getBooleanDecoder()],
    ["transferHook", getOptionDecoder(getAddressDecoder())],
    ["parentAgent", getOptionDecoder(getAddressDecoder())],
    ["generation", getU32Decoder()],
    ["x402Enabled", getBooleanDecoder()],
    ["x402PaymentAddress", getAddressDecoder()],
    ["x402AcceptedTokens", getArrayDecoder(getAddressDecoder())],
    ["x402PricePerCall", getU64Decoder()],
    [
      "x402ServiceEndpoint",
      addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder()),
    ],
    ["x402TotalPayments", getU64Decoder()],
    ["x402TotalCalls", getU64Decoder()],
    ["lastPaymentTimestamp", getI64Decoder()],
    ["externalIdentifiers", getArrayDecoder(getExternalIdentifierDecoder())],
    ["ghostScore", getU64Decoder()],
    ["reputationComponents", getArrayDecoder(getReputationComponentDecoder())],
    ["didAddress", getOptionDecoder(getAddressDecoder())],
    ["credentials", getArrayDecoder(getAddressDecoder())],
    ["apiSpecUri", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["apiVersion", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["bump", getU8Decoder()],
  ]);
}

/** Gets the codec for {@link Agent} account data. */
export function getAgentCodec(): Codec<AgentArgs, Agent> {
  return combineCodec(getAgentEncoder(), getAgentDecoder());
}

export function decodeAgent<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<Agent, TAddress>;
export function decodeAgent<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<Agent, TAddress>;
export function decodeAgent<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<Agent, TAddress> | MaybeAccount<Agent, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getAgentDecoder(),
  );
}

export async function fetchAgent<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<Agent, TAddress>> {
  const maybeAccount = await fetchMaybeAgent(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeAgent<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<Agent, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeAgent(maybeAccount);
}

export async function fetchAllAgent(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<Agent>[]> {
  const maybeAccounts = await fetchAllMaybeAgent(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeAgent(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<Agent>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeAgent(maybeAccount));
}
